import React, { useEffect, useState, useCallback } from "react";
import "../../Details.css";
import "./License.css"; // optional if you create custom styles
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import { useAuth } from "../../../auth/AuthContext";

const LICENSE_API_URL =
  "https://app.tablemate.net/licensor/fetchLicenseInfo.php";
const UPDATE_CONTACT_URL =
  "https://app.tablemate.net/licensor/updateLicenseContact.php";
const RENEW_LICENSE_URL =
  "https://app.tablemate.net/licensor/renewLicense.php";
const UPDATE_STATUS_URL =
  "https://app.tablemate.net/licensor/updateLicenseStatus.php";

function License() {
  const pageTitle = "License";

  const { accessToken, selectedRestaurantId } = useAuth();

  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [actionMessage, setActionMessage] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    contact: "",
    public_phone: "",
    email: "",
    website: "",
  });

  // License key show/hide
  const [showLicenseKey, setShowLicenseKey] = useState(false);

  // -----------------------------
  // Fetch license info
  // -----------------------------
  const loadLicense = useCallback(async () => {
    if (!accessToken || !selectedRestaurantId) return;

    const controller = new AbortController();

    try {
      setLoading(true);
      setLoadError(null);

      const url = `${LICENSE_API_URL}?id=${encodeURIComponent(
        selectedRestaurantId
      )}`;
      console.log("[License] Fetching license:", url);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      if (!res.ok) {
        let bodyText = "";
        try {
          bodyText = await res.text();
        } catch {
          bodyText = "<no body>";
        }
        const msg = `HTTP ${res.status} ${res.statusText} – ${bodyText}`;
        console.warn("[License] loadLicense failed:", msg);
        throw new Error(msg);
      }

      const data = await res.json();
      console.log("[License] License response:", data);

      const row = Array.isArray(data) ? data[0] || null : data || null;
      setLicenseInfo(row);

      if (row) {
        setContactForm({
          name: row.name || "",
          contact: row.contact || "",
          public_phone: row.public_phone || "",
          email: row.email || "",
          website: row.website || "",
        });
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("[License] loadLicense error:", err);
      setLoadError(err.message || "Failed to load license info");
      setLicenseInfo(null);
    } finally {
      setLoading(false);
      controller.abort();
    }
  }, [accessToken, selectedRestaurantId]);

  useEffect(() => {
    loadLicense();
  }, [loadLicense]);

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveContact = async () => {
    if (!accessToken || !selectedRestaurantId) return;
    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    try {
      const res = await fetch(UPDATE_CONTACT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          id: selectedRestaurantId,
          ...contactForm,
        }),
      });

      if (!res.ok) {
        let bodyText = "";
        try {
          bodyText = await res.text();
        } catch {
          bodyText = "<no body>";
        }
        const msg = `HTTP ${res.status} ${res.statusText} – ${bodyText}`;
        console.warn("[License] Save contact failed:", msg);
        throw new Error(msg);
      }

      await loadLicense();
      setActionMessage("Contact information updated successfully.");
    } catch (err) {
      console.warn("[License] handleSaveContact error:", err);
      setActionError(
        err.message || "Failed to update contact information."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenewLicense = async () => {
    if (!accessToken || !selectedRestaurantId) return;
    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    try {
      const res = await fetch(RENEW_LICENSE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          id: selectedRestaurantId,
        }),
      });

      if (!res.ok) {
        let bodyText = "";
        try {
          bodyText = await res.text();
        } catch {
          bodyText = "<no body>";
        }
        const msg = `HTTP ${res.status} ${res.statusText} – ${bodyText}`;
        console.warn("[License] Renew failed:", msg);
        throw new Error(msg);
      }

      await loadLicense();
      setActionMessage("License renewed successfully.");
    } catch (err) {
      console.warn("[License] handleRenewLicense error:", err);
      setActionError(err.message || "Failed to renew license.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!accessToken || !selectedRestaurantId || !licenseInfo) return;
    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    const currentStatus = licenseInfo.licence_status || "Inactive";
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    try {
      const res = await fetch(UPDATE_STATUS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          id: selectedRestaurantId,
          status: newStatus, // backend: update licence_status
        }),
      });

      if (!res.ok) {
        let bodyText = "";
        try {
          bodyText = await res.text();
        } catch {
          bodyText = "<no body>";
        }
        const msg = `HTTP ${res.status} ${res.statusText} – ${bodyText}`;
        console.warn("[License] Update status failed:", msg);
        throw new Error(msg);
      }

      await loadLicense();
      setActionMessage(
        newStatus === "Active"
          ? "License activated."
          : "License deactivated."
      );
    } catch (err) {
      console.warn("[License] handleToggleStatus error:", err);
      setActionError(err.message || "Failed to update license status.");
    } finally {
      setActionLoading(false);
    }
  };

  // -----------------------------
  // Display helpers
  // -----------------------------
  const restaurantIdDisplay =
    licenseInfo?.id ?? selectedRestaurantId ?? "—";

  const statusDisplay = licenseInfo?.licence_status || "Not available";
  const licenceTypeDisplay = licenseInfo?.licence_type || "Standard";

  const startDisplay =
    licenseInfo?.licence_start && licenseInfo.licence_start !== "0000-00-00"
      ? licenseInfo.licence_start
      : "Not set";

  const expiryDisplay =
    licenseInfo?.licence_expiry && licenseInfo.licence_expiry !== "0000-00-00"
      ? licenseInfo.licence_expiry
      : "Not set";

  const devicesDisplay =
    licenseInfo?.devices_licenced != null
      ? licenseInfo.devices_licenced
      : "Not set";

  const planDisplay = licenseInfo?.payment_plan || "Not set";

  const isActive = statusDisplay === "Active";

  const rawLicenseKey = licenseInfo?.licence || null;
  const licenseKeyDisplay =
    showLicenseKey && rawLicenseKey
      ? rawLicenseKey
      : rawLicenseKey
      ? "••••••••••••••••"
      : "Not available";

  return (
    <div className="details-container-page">
      <Navbar pageTitle={pageTitle} onToggleSidebar={true} />

      <div className="details-main">
        {!accessToken || !selectedRestaurantId ? (
          <div style={{ padding: 24 }}>
            <h2>License Information</h2>
            <p>You must be logged in and have a restaurant selected.</p>
          </div>
        ) : (
          <div className="license-layout">
            {/* Left column – summary / actions */}
            <div className="license-column">
              <section className="settings-card">
                <div className="settings-card-header">
                  <h2 className="settings-card-title">License Summary</h2>
                </div>

                {loading && (
                  <div className="settings-field">
                    <span className="settings-label">Loading…</span>
                  </div>
                )}

                {loadError && (
                  <div className="settings-field">
                    <span className="settings-label">Error:</span>
                    <span className="settings-value settings-error">
                      {loadError}
                    </span>
                  </div>
                )}

                {!loading && !loadError && licenseInfo && (
                  <>
                    <div className="settings-field">
                      <span className="settings-label">
                        Restaurant / License ID
                      </span>
                      <span className="settings-value">
                        {restaurantIdDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">License Key</span>
                      <span className="settings-value">
                        {licenseKeyDisplay}
                      </span>
                      {rawLicenseKey && (
                        <button
                          type="button"
                          className="tm-btn tm-btn-light"
                          style={{ marginLeft: "8px" }}
                          onClick={() =>
                            setShowLicenseKey((prev) => !prev)
                          }
                        >
                          {showLicenseKey ? "Hide" : "Show"}
                        </button>
                      )}
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">Status</span>
                      <span className="settings-value">
                        {statusDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">License Type</span>
                      <span className="settings-value">
                        {licenceTypeDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">Start Date</span>
                      <span className="settings-value">
                        {startDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">Expiry / Renewal</span>
                      <span className="settings-value">
                        {expiryDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">
                        Devices Licensed
                      </span>
                      <span className="settings-value">
                        {devicesDisplay}
                      </span>
                    </div>

                    <div className="settings-field">
                      <span className="settings-label">Payment Plan</span>
                      <span className="settings-value">
                        {planDisplay}
                      </span>
                    </div>
                  </>
                )}
              </section>

              <section className="settings-card settings-card-actions">
                <div className="settings-card-header">
                  <h2 className="settings-card-title">License Actions</h2>
                </div>

                <button
                  className="tm-btn tm-btn-primary tm-btn-full"
                  type="button"
                  onClick={handleRenewLicense}
                  disabled={actionLoading || loading || !licenseInfo}
                >
                  Renew License
                </button>

                <button
                  className="tm-btn tm-btn-light tm-btn-full"
                  type="button"
                  onClick={handleToggleStatus}
                  disabled={actionLoading || loading || !licenseInfo}
                >
                  {isActive ? "Deactivate License" : "Activate License"}
                </button>

                <Link
                  to="/settings"
                  className="tm-btn tm-btn-dark tm-btn-full"
                >
                  Back to Settings
                </Link>

                {actionMessage && (
                  <p className="settings-success">{actionMessage}</p>
                )}
                {actionError && (
                  <p className="settings-error">{actionError}</p>
                )}
              </section>
            </div>

            {/* Right column – contact info form */}
            <div className="license-column">
              <section className="settings-card">
                <div className="settings-card-header">
                  <h2 className="settings-card-title">
                    Contact & Listing Information
                  </h2>
                </div>

                <div className="settings-field">
                  <label className="settings-label" htmlFor="name">
                    Restaurant Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    className="settings-input"
                    value={contactForm.name}
                    onChange={handleContactChange}
                  />
                </div>

                <div className="settings-field">
                  <label className="settings-label" htmlFor="contact">
                    Contact Person
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    className="settings-input"
                    value={contactForm.contact}
                    onChange={handleContactChange}
                  />
                </div>

                <div className="settings-field">
                  <label className="settings-label" htmlFor="public_phone">
                    Public Phone
                  </label>
                  <input
                    id="public_phone"
                    name="public_phone"
                    className="settings-input"
                    value={contactForm.public_phone}
                    onChange={handleContactChange}
                  />
                </div>

                <div className="settings-field">
                  <label className="settings-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    className="settings-input"
                    type="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                  />
                </div>

                <div className="settings-field">
                  <label className="settings-label" htmlFor="website">
                    Website
                  </label>
                  <input
                    id="website"
                    name="website"
                    className="settings-input"
                    value={contactForm.website}
                    onChange={handleContactChange}
                  />
                </div>

                <button
                  className="tm-btn tm-btn-primary tm-btn-full"
                  type="button"
                  onClick={handleSaveContact}
                  disabled={actionLoading || loading || !licenseInfo}
                >
                  Save Contact Info
                </button>
              </section> 
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default License;
